import {
  Box,
  Button,
  Flex,
  Text,
  Select as ChakraSelect,
  Select,
} from "@chakra-ui/react";
import PageTitle from "../components/PageTitle";
import { AddIcon } from "@chakra-ui/icons";
import FileIcon from "../components/icons/FileIcon";
import { Command, CommandStatus, CommandType } from "../types/command.types";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { Link, LinkProps, useNavigate } from "react-router-dom";
import DataTable, {
  Cell,
  SearchBar,
} from "../components/table/ServerSideTable";
import { useCommandsPaginated } from "../hooks/commands.hooks";
import { format } from "date-fns";
import { useAdmins, useConnectedUser } from "../hooks/user.hooks";
import i18n from "../i18n";
import { updateCommand } from "../mutations/command.mutations";
import { useMutation, useQueryClient } from "react-query";
import PrimaryButton from "../components/PrimaryButton";
import CartIcon from "../components/icons/CartIcon";
import { NewOrderEarSide, useNewOrderStore } from "../stores/order.store";
import {
  useCenterCommandsPaginated,
  useCenters,
  useCenterStat,
} from "../hooks/centers.hooks";
import Tooltip from "../components/Tooltip";
import EarBadge from "../components/EarBadge";
import CommandTypeBadge from "../components/CommandTypeBadge";
import OrderStatusBadge from "../components/OrderStatusBadge";
import { PaginatedFilter } from "../types/axios.types";
import { Center } from "../types/center.types";
import CommandStatusBadge from "../components/CommandStatusBadge";
import { useDataTableContext } from "../hooks/data-table.hooks";
import CenterIDSelect from "../components/inputs/CenterIDSelect";

const AdminOnlyLink = ({
  children,
  ...props
}: { children: React.ReactNode } & LinkProps) => {
  const user = useConnectedUser();

  if (user?.isSuperAdmin) {
    return <Link {...props}>{children}</Link>;
  }
  return children;
};

function OrdersTableCenterCell({
  centerName,
  centerId,
}: {
  centerName?: string;
  centerId?: number;
}) {
  if (!centerName) {
    return (
      <Text fontSize="14px" color="gray.500">
        ---
      </Text>
    );
  }
  return (
    <Text
      fontSize="14px"
      textTransform="capitalize"
      textColor="brand.700"
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
    >
      <AdminOnlyLink to={`/orders?center=${centerId}`}>
        {centerName}
      </AdminOnlyLink>
    </Text>
  );
}

function OrdersTablePatientCell({
  firstName,
  lastName,
}: {
  firstName?: string;
  lastName?: string;
}) {
  return (
    <Text
      fontSize="14px"
      textTransform="capitalize"
      textColor="brand.700"
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
    >
      {firstName}&nbsp;{lastName}
    </Text>
  );
}

function OrdersTableMaterialTypeCell({ order }: { order: Command }) {
  const variants = order?.draft?.variants;
  const variant = Array.isArray(variants)
    ? variants.find((variant) =>
        variant.label.toLocaleLowerCase().includes("matière")
      )
    : undefined;
  const form = order?.draft?.hasRightEar
    ? order?.draft?.rightEarForm
    : order?.draft?.leftEarForm;
  if (variant === undefined || form === undefined || !(variant.id in form)) {
    return <Text fontSize={"12px"}>---</Text>;
  }
  return <Text fontSize={"12px"}>{form[variant.id]?.label ?? "---"}</Text>;
}

const SelectAdminBadge = ({
  value,
  onChange,
}: {
  value?: number;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}) => {
  const { data: admins } = useAdmins();

  return (
    <ChakraSelect
      bgColor={"color.100"}
      color="color.700"
      fontSize="14px"
      h="21px"
      borderRadius="xl"
      value={value ?? undefined}
      onChange={(evt) => onChange && onChange(evt)}
    >
      <option value="">---</option>
      {admins &&
        admins.map((admin) => (
          <option key={admin.id} value={admin.id ?? undefined}>
            {admin.firstName}
          </option>
        ))}
    </ChakraSelect>
  );
};

const CenterIDSelectFilter = (props: {
  filters?: PaginatedFilter;
  setFilters?: (key: string, value: string) => void;
}) => {
  const user = useConnectedUser();

  if (user?.isSuperAdmin === true) {
    return (
      <div style={{ width: "100px" }}>
        <CenterIDSelect
          placeholder={i18n.t("input.select.center.placeholder")}
          centerSelected={props.filters?.centerId as string}
          setCenterSelected={(center?: Center) => {
            if (props.setFilters) {
              props.setFilters("centerId", center?.id.toString() ?? "");
            }
          }}
        />
      </div>
    );
  }
  return null;
};

const OrdersTableLeftFilters = () => {
  const { filters, setFilters } = useDataTableContext();
  const user = useConnectedUser();
  const { center } = useCenters();
  const { data: stats } = useCenterStat(center?.id);
  const hasDraftFilter = useMemo(() => {
    if (filters === undefined) {
      return false;
    }
    return filters["status"] === "DRAFT";
  }, [filters]);

  if (user?.isSuperAdmin) {
    return (
      <Flex pl={4}>
        {
          <Button
            variant="link"
            px={4}
            color="brand.700"
            backgroundColor={hasDraftFilter ? "brand.50" : undefined}
            onClick={() => {
              if (setFilters) {
                if (hasDraftFilter) {
                  setFilters("status", "");
                  setFilters("not_status", "DRAFT");
                } else {
                  setFilters("status", "DRAFT");
                  setFilters("not_status", "");
                }
              }
            }}
          >
            Brouillons
          </Button>
        }
      </Flex>
    );
  }
  return (
    <Flex pl={4}>
      {stats && (
        <Button
          variant="link"
          color="brand.700"
          backgroundColor={hasDraftFilter ? "brand.50" : undefined}
          onClick={() => {
            if (setFilters) {
              if (hasDraftFilter) {
                setFilters("status", "");
                setFilters("not_status", "DRAFT");
              } else {
                setFilters("status", "DRAFT");
                setFilters("not_status", "");
              }
            }
          }}
        >
          Brouillons ({stats.commandsDraftCount})
        </Button>
      )}
    </Flex>
  );
};

const OrdersTableToolbar = () => {
  const { filters, setFilters } = useDataTableContext();

  return (
    <Flex my="1rem" justifyContent="space-between">
      <Flex my="1rem" justifyContent="space-between" gap={2}>
        <CenterIDSelectFilter filters={filters} setFilters={setFilters} />
        <SearchBar />
        <OrdersTableLeftFilters />
      </Flex>
      <Flex my="1rem" justifyContent="space-between">
        <Flex gap={2}>
          <Link to="/orders">
            <Button variant="ghost" color="brand.700">
              Supprimer les filtres
            </Button>
          </Link>
          <Select
            w="200px"
            placeholder="Statut"
            value={filters?.status}
            onChange={(evt) => {
              if (setFilters) {
                setFilters("status", evt.target.value);
              }
            }}
          >
            {[
              CommandStatus.DRAFT,
              CommandStatus.WAITING_FOR_RECEPTION,
              CommandStatus.RECEIVED,
              CommandStatus.MODELING,
              CommandStatus.PRINTING,
              CommandStatus.INJECTING,
              CommandStatus.POLISHING,
              CommandStatus.QUALITY_CONTROL,
              CommandStatus.READY_FOR_DELIVERY,
              CommandStatus.IN_DELIVERY,
              CommandStatus.DELIVERED,
            ].map((status) => (
              <option key={status} value={status}>
                {i18n.t(`command.status.${status}`)}
              </option>
            ))}
            <option value="HALTED">En pause</option>
            <option value={CommandStatus.ABORTED}>
              {i18n.t(`command.status.${CommandStatus.ABORTED}`)}
            </option>
          </Select>
          <Select
            w="100px"
            placeholder="Type"
            value={filters?.type}
            onChange={(evt) => {
              if (setFilters) {
                setFilters("type", evt.target.value);
              }
            }}
          >
            {Object.values(CommandType).map((type) => (
              <option key={type} value={type}>
                {i18n.t(`command.type.${type}`)}
              </option>
            ))}
          </Select>
        </Flex>
      </Flex>
    </Flex>
  );
};

function OrdersPage() {
  const user = useConnectedUser();
  const { center } = useCenters();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate: mutateUpdateCommand } = useMutation(updateCommand, {
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
  });
  const { reset, load } = useNewOrderStore();
  const commandsQuery = useCommandsPaginated({});
  const centerCommandsQuery = useCenterCommandsPaginated({ id: center?.id });
  const paginatedQuery = user?.isSuperAdmin
    ? commandsQuery
    : centerCommandsQuery;

  const OrdersTableNoData = () => {
    return (
      <Flex justify="center">
        <Flex w="100%" justifyContent="center">
          <Flex
            w="100%"
            rounded="xl"
            bg="white"
            textAlign="center"
            lineHeight="44px"
            color="gray.500"
            direction={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            py="100px"
          >
            <Flex
              w="48px"
              h="48px"
              bg="zinc.50"
              border="solid 1px"
              borderColor="brand.100"
              justifyContent="center"
              alignItems="center"
              borderRadius={100}
              mr="8px"
            >
              <CartIcon w="24px" h="24px" color="brand.700"></CartIcon>
            </Flex>
            <Text
              mt={4}
              color={"gray.900"}
              fontSize={"20px"}
              lineHeight={"30px"}
              fontWeight={600}
            >
              Aucune commande
            </Text>
            <PrimaryButton
              w="30rem"
              mt={4}
              onClick={() => {
                reset();
                navigate("/orders/new");
              }}
            >
              Créer une commande
            </PrimaryButton>
          </Flex>
        </Flex>
      </Flex>
    );
  };
  const columns = useMemo<ColumnDef<Command>[]>(() => {
    const innerColumns: ColumnDef<Command>[] = [
      {
        accessorFn: (row) => row.exposedId,
        cell: (info) => {
          const id = info.row.original.exposedId
            .split("-")
            .slice(0, 3)
            .join("-");
          return (
            <Tooltip content={`${id}`}>
              <Box
                textOverflow={"ellipsis"}
                whiteSpace={"nowrap"}
                overflow={"hidden"}
                fontSize={"14px"}
              >
                {id}
              </Box>
            </Tooltip>
          );
        },
        id: "id",
        header: "ID",
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => row.center.name ?? "---",
        cell: (info) => (
          <OrdersTableCenterCell
            centerName={
              info.row.original.center ? info.row.original.center.name : "---"
            }
            centerId={info.row.original.centerId}
          ></OrdersTableCenterCell>
        ),
        id: "center",
        header: "Centre",
        enableSorting: false,
      },
      {
        accessorFn: (row) => row.patientId,
        cell: (info) => (
          <OrdersTablePatientCell
            firstName={info.row.original.patient?.firstName}
            lastName={info.row.original.patient?.lastName}
          ></OrdersTablePatientCell>
        ),
        id: "patient",
        header: "Patient",
        enableSorting: false,
      },
      {
        accessorFn: (row) => row.status,
        id: "status",
        header: "Statut",
        cell: (info) => {
          if (user?.isSuperAdmin) {
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  margin: "0px 10px 0px 0px",
                }}
              >
                <CommandStatusBadge id={info.row.original.id} editable={true} />
              </div>
            );
          }
          return (
            <div style={{ padding: "0px 12px 0px 0px" }}>
              <OrderStatusBadge
                halted={info.row.original.haltedAt != null}
                status={info.row.original.status}
                width={"100%"}
              ></OrderStatusBadge>
            </div>
          );
        },
        enableSorting: false,
      },
      {
        accessorFn: (row) => row.type,
        id: "type",
        header: "Type",
        cell: (info) => (
          <CommandTypeBadge type={info.row.original.type}></CommandTypeBadge>
        ),
        enableSorting: false,
      },
      {
        accessorFn: (row) => row.type,
        cell: (info) => {
          if (info.row.original.type === CommandType.SUPPLY) {
            return null;
          }
          const draft = info.row.original.draft;
          if (draft === undefined) {
            return null;
          }
          const hasRight = draft?.hasRightEar;
          const hasLeft = draft?.hasLeftEar;
          return (
            <Flex gap={1}>
              {hasRight && <EarBadge side={NewOrderEarSide.RIGHT}></EarBadge>}
              {hasLeft && <EarBadge side={NewOrderEarSide.LEFT}></EarBadge>}
            </Flex>
          );
        },
        id: "ear",
        header: "Oreilles",
        enableSorting: false,
      },
      {
        accessorFn: (row) => row.id,
        cell: (info) => (
          <OrdersTableMaterialTypeCell order={info.row.original} />
        ),
        id: "material",
        header: "Matériau",
        enableSorting: false,
      },
      {
        accessorFn: (row) => row.deliveredAt,
        cell: (info) => {
          if (info.row.original.deliveredAt) {
            return (
              <Text fontSize={"12px"}>
                {format(info.row.original.deliveredAt, "dd/MM/yyyy")}
              </Text>
            );
          }
          return null;
        },
        id: "deliveredAt",
        header: "Livraison",
        sortDescFirst: true,
      },
    ];
    if (user?.isSuperAdmin) {
      innerColumns.push({
        accessorFn: (row) => row.managerId,
        cell: (info) => (
          <SelectAdminBadge
            value={info.row.original.managerId}
            onChange={(event) => {
              mutateUpdateCommand({
                id: info.row.original.id,
                managerId:
                  event.target.value !== ""
                    ? Number.parseInt(event.target.value)
                    : undefined,
              });
            }}
          ></SelectAdminBadge>
        ),
        id: "managerId",
        header: "Assigné à",
        enableSorting: false,
      });
    }
    return innerColumns;
  }, [mutateUpdateCommand, user]);

  return (
    <>
      <Flex w="100%" justifyContent="space-between" pb={4}>
        <PageTitle display="inline">Commandes</PageTitle>
        <Flex gap={3}>
          {user?.role !== "ADMIN" && (
            <Button
              variant="outline"
              onClick={() => navigate("/orders/supplies")}
            >
              <FileIcon mr={3}></FileIcon>
              Acheter des fournitures
            </Button>
          )}
          <Button
            bg="brand.700"
            color="white"
            variant="solid"
            onClick={() => {
              reset();
              navigate("/orders/new");
            }}
          >
            <AddIcon mr={3}></AddIcon> Créer une commande
          </Button>
        </Flex>
      </Flex>

      {(user?.isSuperAdmin || center !== null) && (
        <DataTable
          columns={columns}
          defaultSortBy={[
            {
              id: "id",
              desc: true,
            },
          ]}
          defaultFilters={{
            not_status: "DRAFT",
          }}
          paginatedQuery={paginatedQuery}
          w={[
            "30px",
            "160px",
            "160px",
            "200px",
            "100px",
            "100px",
            "100px",
            "100px",
            "10rem",
          ]}
          flexGrow={[1, 1, 1, 0, 0, 0, 1, 0]}
          cellLink={(cell: Cell) => {
            if (cell.row.status === CommandStatus.DRAFT) {
              return undefined;
            }
            if (cell.id.endsWith("managerId") || cell.id.endsWith("status")) {
              return undefined;
            }
            return `/orders/${cell.row.id}`;
          }}
          handleCellClick={(cell: Cell) => {
            if (cell.row.status === CommandStatus.DRAFT) {
              load({
                ...cell.row.draft,
                command: { ...cell.row, draft: undefined },
              });
              navigate("/orders/new");
            }
          }}
          noDataComponent={OrdersTableNoData}
          toolbar={<OrdersTableToolbar />}
        ></DataTable>
      )}
    </>
  );
}

export default OrdersPage;
